import { useId } from 'react'
import { Label, Switch } from '@teach/ui'
import type { CourseUpdate, CourseWithoutBlocks } from '@/types/course'
import { useAppDispatch } from '@/store/hooks'
import { coursesApi, useUpdateCourseMutation } from '../../coursesApi'
import { useMessages } from '@/i18n'
import { messages } from './messages'

type HiddenKey = 'isHiddenOnWeb' | 'isHiddenOnIos' | 'isHiddenOnAndroid' | 'isHiddenOnTma'

// `label: null` — подпись по языку интерфейса (`platformWeb`), остальные — имена собственные.
const PLATFORMS: { key: HiddenKey; label: string | null }[] = [
  { key: 'isHiddenOnWeb', label: null },
  { key: 'isHiddenOnIos', label: 'iOS' },
  { key: 'isHiddenOnAndroid', label: 'Android' },
  { key: 'isHiddenOnTma', label: 'Telegram Mini App' },
]

interface Props {
  course: CourseWithoutBlocks
  disabled: boolean
}

export const PlatformVisibility = ({ course, disabled }: Props) => {
  const m = useMessages(messages)
  const groupId = useId()
  const [updateCourse] = useUpdateCourseMutation()
  const dispatch = useAppDispatch()

  // Значение — из курса на каждом рендере (api отдаёт флаги соавторам и админу). Оптимистика —
  // в кеше getEditorCourse, и PUT-инвалидация её не роняет: рефетч принесёт то же значение.
  // Провал откатывает патч кеша; тост показывает глобальный middleware.
  const toggle = (key: HiddenKey, shown: boolean) => {
    const patch = dispatch(
      coursesApi.util.updateQueryData('getEditorCourse', course.id, (draft) => {
        draft[key] = !shown
      }),
    )
    void updateCourse({ id: course.id, patch: { [key]: !shown } as CourseUpdate })
      .unwrap()
      .catch(() => patch.undo())
  }

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{m.showOnPlatforms}</Label>
      <div className="rounded-xl border border-border divide-y divide-border">
        {PLATFORMS.map(({ key, label }) => (
          <div key={key} className="flex items-center justify-between px-3 py-2.5">
            <Label htmlFor={`${groupId}-${key}`}>{label ?? m.platformWeb}</Label>
            <Switch
              id={`${groupId}-${key}`}
              checked={!(course[key] ?? false)}
              onCheckedChange={(shown) => toggle(key, shown)}
              disabled={disabled}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
